import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const seoPlans = [
  {
    title: "SEO Audit & Fixes",
    serviceKey: "seo_audit_fixes",
    price: 4999,
    cadence: "one-time",
    bestFor: "new or recently redesigned websites",
    features: [
      "Technical crawl review",
      "Metadata and heading fixes",
      "Sitemap and robots.txt review",
      "Search Console setup guidance",
    ],
  },
  {
    title: "Local SEO Growth",
    serviceKey: "local_seo_growth",
    price: 8999,
    cadence: "monthly",
    bestFor: "local service businesses",
    features: [
      "Local keyword mapping",
      "Google Business Profile checklist",
      "Service page optimization",
      "Monthly performance report",
    ],
  },
  {
    title: "Content SEO Retainer",
    serviceKey: "content_seo_retainer",
    price: 14999,
    cadence: "monthly",
    bestFor: "businesses publishing regularly",
    features: [
      "Content calendar",
      "On-page optimization",
      "Internal linking plan",
      "Structured data recommendations",
    ],
  },
];

const workflow = [
  {
    title: "Audit",
    body: "Check crawlability, page titles, descriptions, index directives, sitemap, robots.txt, headings, and structured data.",
  },
  {
    title: "Fix",
    body: "Prioritize technical issues first, then improve page copy, internal links, schema, and conversion sections.",
  },
  {
    title: "Measure",
    body: "Track Search Console, analytics, conversions, indexed pages, and monthly query movement instead of vanity promises.",
  },
];

const normalizeWebsite = (value) =>
  value
    .trim()
    .replace(/\s+/g, "")
    .replace(/\/+$/, "");

const isLikelyWebsite = (value) =>
  /^(https?:\/\/)?([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}(\/.*)?$/i.test(value);

const getStatusStyle = (status) => {
  if (status === "pass") {
    return "border-emerald-400/30 bg-emerald-500/10 text-emerald-300";
  }

  if (status === "fail") {
    return "border-rose-400/30 bg-rose-500/10 text-rose-300";
  }

  return "border-amber-400/30 bg-amber-500/10 text-amber-300";
};

const Seo = () => {
  const navigate = useNavigate();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");

  const cleanedWebsite = useMemo(() => normalizeWebsite(websiteUrl), [websiteUrl]);
  const canAudit = isLikelyWebsite(cleanedWebsite);

  const runSeoAudit = async () => {
    if (!canAudit) {
      setAuditError("Enter a valid public website URL such as example.com.");
      return;
    }

    setAuditLoading(true);
    setAudit(null);
    setAuditError("");

    try {
      const response = await fetch(
        buildApiUrl(`/api/seo-audit?url=${encodeURIComponent(cleanedWebsite)}`)
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to audit this website.");
      }

      setAudit(data);
    } catch (error) {
      setAuditError(error.message);
    } finally {
      setAuditLoading(false);
    }
  };

  const startCheckout = (plan) => {
    navigate("/payment/razorpay", {
      state: {
        orderType: "seo",
        serviceCategory: "seo",
        serviceKey: plan.serviceKey,
        amount: plan.price,
        domainName: cleanedWebsite.replace(/^https?:\/\//i, "").split("/")[0],
        domainSource: "external",
        setupPreference: plan.title,
        notes: cleanedWebsite ? `SEO service for ${cleanedWebsite}` : "",
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 px-6 py-28 text-white">
      <div className="mx-auto max-w-7xl">
        <section className="grid items-center gap-12 lg:grid-cols-[1fr_430px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-300">
              Search Engine Optimization
            </p>

            <h1 className="mt-5 text-5xl font-black leading-tight md:text-7xl">
              SEO That Starts With
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                {" "}Reality
              </span>
            </h1>

            <p className="mt-8 max-w-4xl text-lg leading-8 text-slate-300">
              We do not sell guaranteed rankings. We improve crawlability, page quality,
              local relevance, structured data, content clarity, and measurement so your
              website has a stronger chance to earn qualified search traffic.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#seo-audit"
                className="rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-7 py-4 text-sm font-bold text-slate-950 transition hover:scale-105"
              >
                Run Website Audit
              </a>

              <a
                href="#seo-plans"
                className="rounded-2xl border border-white/20 bg-white/10 px-7 py-4 text-sm font-bold text-white transition hover:bg-white/20"
              >
                View SEO Plans
              </a>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl"
          >
            <h2 className="text-2xl font-black">What SEO Work Includes</h2>
            <div className="mt-6 space-y-4">
              {[
                ["Technical", "Crawl, index, sitemap, robots, redirects, speed signals"],
                ["On-page", "Titles, descriptions, headings, internal links, schema"],
                ["Content", "Search intent, service pages, useful copy, topic gaps"],
                ["Reporting", "Search Console, analytics, leads, monthly actions"],
              ].map(([title, body]) => (
                <div key={title} className="rounded-2xl bg-slate-950/55 p-4">
                  <h3 className="font-bold text-emerald-300">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{body}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        <section id="seo-audit" className="mt-20 rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-3xl font-black md:text-5xl">Website SEO Snapshot</h2>
              <p className="mt-4 max-w-4xl text-slate-300">
                This checks basic technical signals. It is not a ranking guarantee, but it
                shows whether the website is ready for serious SEO work.
              </p>
            </div>

            {audit ? (
              <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-6 py-4 text-center">
                <p className="text-sm text-slate-300">Technical Score</p>
                <p className="text-4xl font-black text-emerald-300">{audit.score}%</p>
              </div>
            ) : null}
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_auto]">
            <input
              type="text"
              value={websiteUrl}
              onChange={(event) => setWebsiteUrl(event.target.value)}
              placeholder="example.com"
              className="h-14 rounded-2xl border border-white/10 bg-slate-950/60 px-5 text-white outline-none placeholder:text-slate-400 focus:border-emerald-400"
            />

            <button
              type="button"
              onClick={runSeoAudit}
              disabled={!canAudit || auditLoading}
              className="h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-7 text-sm font-bold text-slate-950 transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {auditLoading ? "Auditing..." : "Run Audit"}
            </button>
          </div>

          {auditError ? (
            <p className="mt-4 text-sm font-semibold text-rose-300">{auditError}</p>
          ) : null}

          {audit ? (
            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {audit.checks.map((check) => (
                <article
                  key={check.key}
                  className={`rounded-2xl border p-5 ${getStatusStyle(check.status)}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-bold text-white">{check.label}</h3>
                    <span className="text-sm font-bold uppercase">{check.status}</span>
                  </div>
                  <p className="mt-3 break-words text-sm text-slate-300">{check.detail}</p>
                </article>
              ))}

              <article className="rounded-2xl border border-white/10 bg-slate-950/55 p-5 md:col-span-2 xl:col-span-3">
                <h3 className="text-xl font-bold text-emerald-300">Snapshot Summary</h3>
                <div className="mt-4 grid gap-4 text-sm text-slate-300 md:grid-cols-2">
                  <p>Title: {audit.summary.title || "Missing"}</p>
                  <p>Canonical: {audit.summary.canonical || "Missing"}</p>
                  <p>H1 count: {audit.summary.h1Count}</p>
                  <p>Structured data blocks: {audit.summary.structuredDataCount}</p>
                  <p>Images with alt text: {audit.summary.imageAltCount}/{audit.summary.imageCount}</p>
                  <p>Page size: {Math.round(audit.summary.contentLength / 1024)} KB</p>
                </div>
                <p className="mt-4 text-xs text-slate-400">{audit.disclaimer}</p>
              </article>
            </div>
          ) : null}
        </section>

        <section className="mt-20 grid gap-6 md:grid-cols-3">
          {workflow.map((item) => (
            <article
              key={item.title}
              className="rounded-[30px] border border-white/10 bg-white/10 p-7 backdrop-blur-xl"
            >
              <h3 className="text-2xl font-black text-white">{item.title}</h3>
              <p className="mt-4 leading-7 text-slate-300">{item.body}</p>
            </article>
          ))}
        </section>

        <section id="seo-plans" className="mt-24">
          <h2 className="text-center text-4xl font-black md:text-6xl">SEO Services</h2>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {seoPlans.map((plan) => (
              <motion.article
                key={plan.serviceKey}
                whileHover={{ y: -8, scale: 1.03 }}
                className="rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl"
              >
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-300">
                  {plan.bestFor}
                </p>
                <h3 className="mt-4 text-3xl font-black">{plan.title}</h3>
                <p className="mt-6 text-5xl font-black text-emerald-300">
                  Rs. {plan.price}
                  <span className="text-base font-semibold text-slate-300">/{plan.cadence}</span>
                </p>

                <div className="mt-8 space-y-4 text-slate-300">
                  {plan.features.map((feature) => (
                    <p key={feature}>{feature}</p>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => startCheckout(plan)}
                  className="mt-10 w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 py-4 font-bold text-slate-950 transition-all duration-300 hover:scale-105"
                >
                  Buy SEO Service
                </button>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="mt-20 rounded-[35px] border border-amber-300/20 bg-amber-500/10 p-8">
          <h2 className="text-2xl font-black text-amber-200">What We Do Not Promise</h2>
          <p className="mt-4 leading-7 text-slate-300">
            No agency can honestly guarantee a specific Google ranking or instant traffic.
            We avoid spam links, hidden text, keyword stuffing, doorway pages, and other
            risky shortcuts. The realistic promise is disciplined implementation,
            transparent reporting, and steady improvement.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Seo;
